import React from 'react';
import { Box, Typography } from '@mui/material';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface HousesAndRoomsProps {
  data: {
    total_launched: {
      value: number;
      label: string;
    };
    next_month: {
      value: number;
      label: string;
    };
    next_house_launched: {
      value: string;
      label: string;
      path?: string;
    };
    total_number_houses: {
      value: number;
      label: string;
    };
    total_number_rooms: {
      value: number;
      label: string;
    };
    rooms_will_launch: {
      value: number;
      label: string;
    };
    rooms_for_rent: {
      value: number;
      label: string;
    };
    days_in_maintenance: {
      value: number;
      label: string;
    };
  };
}

const StatItem: React.FC<{
  item: {
    value: number | string;
    label: string;
    path?: string;
  };
  showTrend?: boolean;
}> = ({ item, showTrend }) => {
  const value = item?.value || 0;
  const label = item?.label || '';
  const path = item?.path || undefined;

  const formattedValue =
    typeof value === 'string' && value.includes('-') ? format(new Date(value), 'd MMM') : value;

  return (
    <Box
      className="mb-4"
      onClick={path ? () => window.open(path, '_new') : undefined}
      style={{ cursor: path ? 'pointer' : 'default' }}
    >
      <Box className="flex items-center gap-2">
        <Typography variant="lead1" className="text-[16px] font-bold leading-[24px] text-[#212529]">
          {formattedValue ?? 0}
        </Typography>
        {showTrend && (
          <IconSaxIcon name="trend-up" size={24} strokeWidth={1.5} strokeColor="none" />
        )}
      </Box>
      <Typography variant="lead1" className="text-[12px] text-[#6C757D]">
        {label}
      </Typography>
    </Box>
  );
};

export const HousesAndRoomsCard: React.FC<HousesAndRoomsProps> = ({ data }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        backgroundColor: 'white',
      }}
      className="rounded-lg p-4 shadow-small"
    >
      <Typography
        variant="lead1"
        className="mb-6 font-Vollkorn text-lead1 font-bold leading-[27px] text-[#111827]"
      >
        {t('js.features.admin.stats.dashboard.houses_and_rooms_card.title')}
      </Typography>

      <Box className="mt-3 grid grid-cols-2">
        <Box className="h-full border-r-[1px] border-solid border-N300 pr-6">
          <StatItem item={data.total_number_houses} />
          <StatItem item={data.total_launched} />
          <StatItem item={data.next_month} />
          <StatItem item={data.next_house_launched} />
        </Box>

        <Box className="ml-4">
          <StatItem item={data.days_in_maintenance} showTrend />
          <StatItem item={data.total_number_rooms} showTrend />
          <StatItem item={data.rooms_will_launch} showTrend />
          <StatItem item={data.rooms_for_rent} showTrend />
        </Box>
      </Box>
    </Box>
  );
};
